import React from "react"
import Layout from "../../../components/Layout"
import PageHero from "../../../components/PageHero"
import News from "../../../components/News"
import "../../../styles/news.scss"
import "../../../styles/caseStudy.scss"
import heroImg from "../../../images/caseStudies/MuscatMediaGroupoman.jpg"
import clientLogo from "../../../images/clients/Colored/mediamuscatgroup.jpg"

const CHALLENGES = [
  {
    title: "Difficult Hiring Process",
    desc: "The entire hiring lifecycle — creating JDs, posting ads, shortlisting, interviewing, and hiring — is challenging in the post-COVID world.",
  },
  {
    title: "Overwhelming for HR",
    desc: "These manual processes are too daunting for the HR department, consuming time and focus that should go to people management.",
  },
  {
    title: "Reliance on Spreadsheets",
    desc: "Everything, including core hiring and workforce management tasks, is performed using spreadsheets, which is inefficient and error-prone.",
  },
  {
    title: "Time-Consuming Data Management",
    desc: "Managing, documenting, and reporting on crucial workforce data — leaves, attendance, salary, benefits — takes excessive time.",
  },
]

const SOLUTIONS = [
  { num: "01", text: "Leave Management & Encashment", desc: "Employees apply for leave and can encash unused balances based on company policy — fully automated and policy-driven." },
  { num: "02", text: "Attendance Management & Odoo Integration", desc: "Attendance is captured via biometric/device logs and synced directly into Odoo; attendance policies are also implemented." },
  { num: "03", text: "Gratuity Management", desc: "End-of-service benefits are calculated automatically based on tenure and salary rules, eliminating manual computation errors." },
  { num: "04", text: "Resignation Process", desc: "Employees submit resignations digitally, and the system manages approvals, notice periods, and final settlement steps." },
  { num: "05", text: "Expense Management", desc: "Employees submit claims with receipts, and managers approve them for reimbursement through a streamlined digital workflow." },
  { num: "06", text: "Employee Self-Service Portal", desc: "Staff manage leave, documents, requests, and payslips through a self-service interface — reducing HR workload significantly." },
]

const RESULTS = [
  { metric: "100%", label: "HR workflow automated — complete migration from manual spreadsheets to a digital Odoo system" },
  { metric: "6 Modules", label: "Custom Odoo modules built from scratch to address exact HR department pain points" },
  { metric: "Minutes", label: "HR reports that previously took hours can now be generated in minutes with live data" },
]

const RESULT_BULLETS = [
  "Real-time data availability enables the HR team to create accurate and compelling reports instantly",
  "Employees gain complete self-service access to leave management, payslips, and documents",
  "Automated workflows eliminate manual bottlenecks across the entire employee lifecycle",
]

const MuscatMediaGroup = () => (
  <div className="CaseStudeies">
    <Layout
      pageTitle="Visiomate - Muscat Media Group Case Study"
      descrip="Automated HR Management System for Muscat Media Group — built on Odoo 13 by Visiomate, covering leave, attendance, gratuity, and self-service."
    >
      <PageHero
        eyebrow="Customer Story"
        title="Muscat Media Group"
        description="Automated HR management system for Oman's leading media conglomerate"
      />

      {/* About */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner cs-intro">
          <div className="cs-intro__img">
            <img src={heroImg} alt="Muscat Media Group" />
          </div>
          <div className="cs-intro__text">
            <img src={clientLogo} alt="Muscat Media Group logo" className="cs-client-logo" />
            <h2 className="vm-news-heading vm-news-heading--dark">
              HR Management System for Muscat Media Group
            </h2>
            <p className="vm-news-sub vm-news-sub--dark">
              The Muscat Media Group (MMG) is the leading media group in the Sultanate of Oman. The
              group has played a vibrant role in Omani society since 1975. MMG offers advertisers more
              than 32 products in Arabic and English, including the Times of Oman, Al Shabiba, and
              Tweekly. Visiomate helped MMG by providing a fully automated HR management system built
              on Odoo 13.
            </p>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What They Were Up Against</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              Before working with Visiomate, MMG's HR department faced four critical operational gaps.
            </p>
          </div>
          <div className="cs-challenge-grid">
            {CHALLENGES.map(c => (
              <div key={c.title} className="cs-challenge-card">
                <div className="cs-challenge-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h3 className="cs-challenge-card__title">{c.title}</h3>
                <p className="cs-challenge-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="vm-news-section vm-news-section--light">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">What We Built</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              We built and customized the following modules from scratch, ensuring they address the
              exact pain points of the HR department.
            </p>
          </div>
          <div className="cs-solution-layout">
            <div className="cs-solutions-grid">
              {SOLUTIONS.map(s => (
                <div key={s.num} className="cs-solution-item">
                  <div className="cs-solution-item__badge">{s.num}</div>
                  <div>
                    <span className="cs-solution-item__text">{s.text}</span>
                    <p className="cs-solution-item__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="cs-solution-img">
              <img src={heroImg} alt="Muscat Media Group" />
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="vm-news-section vm-news-section--gray">
        <div className="vm-news-inner">
          <div className="vm-news-header">
            <h2 className="vm-news-heading vm-news-heading--dark">A Dramatic Transformation</h2>
            <p className="vm-news-sub vm-news-sub--dark">
              MMG's HR department saw measurable impact across every part of the workflow.
            </p>
          </div>
          <div className="cs-results-grid">
            {RESULTS.map(r => (
              <div key={r.metric} className="cs-result-card">
                <span className="cs-result-card__metric">{r.metric}</span>
                <p className="cs-result-card__label">{r.label}</p>
              </div>
            ))}
            {RESULT_BULLETS.map(b => (
              <div key={b} className="cs-result-card cs-result-card--bullet">
                <div className="cs-result-card__icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <p className="cs-result-card__label">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      {/* <section className="vm-news-section vm-news-section--dark">
        <div className="vm-news-inner cs-conclusion">
          <h2 className="vm-news-heading">The Outcome</h2>
          <p className="vm-news-sub">
            This shift from manual to automated system resulted in data availability and accuracy for
            the HR team, who can now use it to create compelling reports in a matter of minutes. What
            was once a burden of spreadsheets and manual tracking is now a seamless, automated
            workflow — positioning MMG's HR department as a modern, data-driven function within
            Oman's leading media group.
          </p>
        </div>
      </section> */}

      <News isAbout={true} />
    </Layout>
  </div>
)

export default MuscatMediaGroup
